/**
 * End-of-session summary (brief §practice: score %, one line per question). The
 * follow-up for the wrong ones serves fresh related problems, not the same ids
 * again (learning-evidence decision #10): `GET /api/practice/related` with the
 * wrong questions, excluding everything this session held — both cut to the
 * route's caps, so a long session never turns the button into a 400.
 */
import { CheckCircle2, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Badge, Button, Card, useToast } from "@client/components/ui";
import { useLang, useT } from "@client/i18n";
import { messageForError } from "@client/lib/errors";
import { formatNumber, formatPercent } from "@client/lib/format";
import { useLocalizedText } from "@client/components/Localized";
import { previewStatement } from "@client/lib/preview";
import { useRelatedQuestions } from "./api";
import { createSession, sessionScore, type PracticeSessionData } from "./session";
import { isTodayReturn } from "./today/session";

/** `GET /api/practice/related` caps (`practice/routes.ts`): more ids answer 400. */
const RELATED_MAX_TARGETS = 50;
const RELATED_MAX_EXCLUDE = 200;

export interface SessionSummaryProps {
  session: PracticeSessionData;
  /** Replaces the active session in place (no navigation needed — same route). */
  onRestart: (session: PracticeSessionData) => void;
}

export function SessionSummary({ session, onRestart }: SessionSummaryProps) {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const toast = useToast();
  const localize = useLocalizedText();
  const relatedMutation = useRelatedQuestions();
  const [noneLeft, setNoneLeft] = useState(false);
  const { score, maxScore } = sessionScore(session);
  // The stored session is not trusted: only a path back to Today's runner is followed.
  const returnTo = isTodayReturn(session.returnTo) ? session.returnTo : undefined;
  const ratio = maxScore > 0 ? score / maxScore : 0;

  // Review-A Important 7: the final "Nästa" unmounts the last question's
  // whole panel — same root cause as UX-03 elsewhere in this feature — and
  // `PracticeSessionPage` renders this component in its place only once
  // `current` has gone from defined to undefined, so this component's own
  // mount *is* that transition. Focus the heading then, the same pattern
  // `FeedbackPanel`'s outcome headline uses.
  const headingRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // `correct` is the first try's, so a miss corrected on the retry still counts as a miss here.
  const wrongQuestions = session.questions.filter((q) => session.results[q.id]?.correct === false);
  const retriedCount = session.questions.filter((q) => session.results[q.id]?.retried).length;
  // A session grows past the setup limit of 30 (each related problem can insert another), so
  // stay inside the route's caps rather than have every click refused with a 400.
  const targets = wrongQuestions.slice(0, RELATED_MAX_TARGETS);

  async function practiseSimilar() {
    if (targets.length === 0) return;
    try {
      const res = await relatedMutation.mutateAsync({
        questionIds: targets.map((q) => q.id),
        exclude: session.questions.slice(0, RELATED_MAX_EXCLUDE).map((q) => q.id),
      });
      const fresh = res.related.flatMap((entry) => (entry.question ? [entry.question] : []));
      if (fresh.length === 0) {
        setNoneLeft(true);
        return;
      }
      // A follow-up of Today's step keeps the way back to the day's session.
      onRestart(createSession(session.mode, fresh, undefined, returnTo ? { returnTo } : {}));
    } catch (error) {
      toast.error(messageForError(error, t));
    }
  }

  return (
    <Card
      title={
        <span ref={headingRef} tabIndex={-1} className="outline-none">
          {t("practice.summary.title")}
        </span>
      }
    >
      <p className="text-3xl font-display text-ink-900 dark:text-ink-50">
        {formatPercent(ratio, lang)}
      </p>
      <p className="mt-1 text-sm text-ink-600 dark:text-ink-400">
        {t("practice.summary.score", {
          score: formatNumber(score, lang, { maxDecimals: 2 }),
          maxScore: formatNumber(maxScore, lang, { maxDecimals: 2 }),
        })}
      </p>

      {retriedCount > 0 && (
        <p className="mt-1 text-sm text-ink-600 dark:text-ink-400">
          {t("practice.summary.firstTry")}
        </p>
      )}

      <ul className="mt-6 space-y-2">
        {session.questions.map((question) => {
          const result = session.results[question.id];
          const correct = result?.correct;
          return (
            <li key={question.id} className="flex items-center gap-2 text-sm">
              {result?.gaveUp ? (
                <Badge tone="neutral">{t("practice.summary.gaveUp")}</Badge>
              ) : result?.retried?.correct ? (
                // Wrong first, right second: the score above counts the first try (§13 Q12).
                <Badge tone="accent">{t("practice.summary.withHelp")}</Badge>
              ) : correct ? (
                <CheckCircle2
                  size={16}
                  className="shrink-0 text-green-600 dark:text-green-400"
                  aria-hidden="true"
                />
              ) : (
                <XCircle
                  size={16}
                  className="shrink-0 text-red-600 dark:text-red-400"
                  aria-hidden="true"
                />
              )}
              <span className="truncate text-ink-700 dark:text-ink-200">
                {previewStatement(localize(question.statement))}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {returnTo && (
          <Button onClick={() => navigate(returnTo)}>{t("practice.summary.backToToday")}</Button>
        )}
        <Button
          variant="secondary"
          onClick={() => void practiseSimilar()}
          loading={relatedMutation.isPending}
          disabled={targets.length === 0 || noneLeft}
        >
          {t("practice.summary.tryRelated", { count: String(targets.length) })}
        </Button>
        <Button variant={returnTo ? "secondary" : "primary"} onClick={() => navigate("/practice")}>
          {t("practice.summary.newSession")}
        </Button>
        <p role="status" className="basis-full text-sm text-ink-600 dark:text-ink-400">
          {noneLeft ? t("practice.summary.noRelated") : ""}
        </p>
      </div>
    </Card>
  );
}
